import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  MarkerType,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useQuery } from '@tanstack/react-query'

import { ALL_CLASSES } from '@/lib/ontology/schema'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'
import { ClassNode } from './nodes/ClassNode'
import { InstanceNode } from './nodes/InstanceNode'
import { FacetNode, type FacetNodeData } from './nodes/FacetNode'
import { useInstancesOfClass, useInstanceDetails, useGraphData, useAllInstanceCounts } from '@/hooks/useInstances'
import { useEntityFacets } from '@/hooks/useFacetGraph'
import { useConnectionStore } from '@/stores/connectionStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LinkedFacetsList } from '@/components/sources/LinkedFacetCard'
import { getLocalName, getDomainFromUri } from '@/lib/ontology/namespaces'
import { FACET_TYPE_CONFIG, type FacetType } from '@/types/interviewer'
import {
  Loader2,
  ArrowRight,
  ArrowLeft,
  X,
  ChevronRight,
  ChevronDown,
  FileText,
  Network,
  GitBranch,
  Eye,
  EyeOff,
  CircleDot,
  Layout,
  Layers,
  RotateCw,
} from 'lucide-react'
import {
  calculateLayout,
  type LayoutAlgorithm,
  type LayoutDirection,
  type LayoutConfig,
  type GraphNode,
  type GraphEdge,
} from '@/lib/graph/layouts'
import {
  ONTOLOGY_CATEGORIES,
  getCategoriesForDomain,
  getCategoryForClass,
  type OntologyCategory,
} from '@/lib/ontology/categories'

// View mode type
type GraphViewMode = 'schema' | 'knowledge' | 'radial'

const nodeTypes = {
  classNode: ClassNode,
  instanceNode: InstanceNode,
  facetNode: FacetNode,
}

const DOMAIN_COLORS: Record<OntologyDomain, string> = {
  core: '#d4a020',    // Amber for core/facet classes
  market: '#5c8a6b',
  business: '#3196e2',
  solution: '#ffc05f',
}

// Domain Boundaries Background Component
// Renders as custom nodes that appear behind other nodes
function createDomainBoundaryNodes(
  bounds: Record<OntologyDomain, { x: number; y: number; width: number; height: number }> | undefined,
  show: boolean
): Node[] {
  if (!show || !bounds) return []

  const boundaryNodes: Node[] = []

  ;(['core', 'market', 'business', 'solution'] as OntologyDomain[]).forEach((domain) => {
    const bound = bounds[domain]
    if (!bound || bound.width === 0 || bound.height === 0) return

    const color = DOMAIN_COLORS[domain]

    boundaryNodes.push({
      id: `domain-boundary-${domain}`,
      type: 'default',
      position: { x: bound.x, y: bound.y },
      data: { label: '' },
      style: {
        width: bound.width,
        height: bound.height,
        background: `${color}0D`, // 5% opacity
        border: `2px dashed ${color}4D`, // 30% opacity
        borderRadius: '12px',
        zIndex: -1,
        pointerEvents: 'none',
      },
      draggable: false,
      selectable: false,
      focusable: false,
    })
  })

  return boundaryNodes
}

// Convert OntologyClass to GraphNode
function classesToGraphNodes(
  classes: OntologyClass[],
  instanceCounts: Record<string, number>
): GraphNode[] {
  return classes.map((cls) => ({
    uri: cls.uri,
    label: cls.localName,
    domain: cls.domain,
    properties: cls.properties.length,
    instanceCount: instanceCounts[cls.uri] || 0,
  }))
}

// Extract edges from classes
function classesToGraphEdges(classes: OntologyClass[]): GraphEdge[] {
  const classUris = new Set(classes.map((c) => c.uri))
  const edges: GraphEdge[] = []

  classes.forEach((cls) => {
    cls.properties
      .filter((p) => p.type === 'object' && classUris.has(p.range))
      .forEach((prop) => {
        edges.push({
          source: cls.uri,
          target: prop.range,
          label: prop.localName,
        })
      })
  })

  return edges
}

interface OntologyGraphProps {
  filterDomain?: OntologyDomain | null
}


export function OntologyGraph({ filterDomain }: OntologyGraphProps) {
  const { isConnected } = useConnectionStore()
  const reactFlowInstanceRef = useRef<any>(null)
  const [viewMode, setViewMode] = useState<GraphViewMode>('schema')
  const [showFacets, setShowFacets] = useState(true)
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set())
  const [selectedNodeUri, setSelectedNodeUri] = useState<string | null>(null)
  const [selectedNodeType, setSelectedNodeType] = useState<'class' | 'instance' | 'facet' | null>(null)

  // Layout configuration state
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('optimized')
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('LR')
  const [showDomainBounds, setShowDomainBounds] = useState(true)
  const [layoutKey, setLayoutKey] = useState(0) // Force re-layout

  // Category filtering state
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())

  // Loading state for layout calculations
  const [isCalculatingLayout, setIsCalculatingLayout] = useState(false)

  // Fetch knowledge graph data when in knowledge or radial mode
  const { data: graphData, isLoading: graphLoading } = useGraphData(
    isConnected && (viewMode === 'knowledge' || viewMode === 'radial'),
    300
  )

  // Get filtered classes by domain and categories
  const filteredClasses = useMemo(() => {
    let classes = filterDomain
      ? ALL_CLASSES.filter((c) => c.domain === filterDomain)
      : ALL_CLASSES

    // Filter by selected categories if any are selected
    if (selectedCategories.size > 0) {
      classes = classes.filter((cls) => {
        const category = getCategoryForClass(cls.localName)
        return category && selectedCategories.has(category.id)
      })
    }

    return classes
  }, [filterDomain, selectedCategories])

  // Fetch instance counts for all classes in a single batched query
  const classUris = useMemo(() => filteredClasses.map((c) => c.uri), [filteredClasses])
  const { data: instanceCounts = {} } = useAllInstanceCounts(classUris, isConnected)

  // Prepare graph data for layout calculation
  const { graphNodes, graphEdges } = useMemo(() => {
    return {
      graphNodes: classesToGraphNodes(filteredClasses, instanceCounts),
      graphEdges: classesToGraphEdges(filteredClasses),
    }
  }, [filteredClasses, instanceCounts])

  // Calculate layout with selected algorithm
  const { positions, domainBounds } = useMemo(() => {
    // Show loading indicator for expensive layouts
    if (layoutAlgorithm === 'force' || layoutAlgorithm === 'dagre') {
      setIsCalculatingLayout(true)
    }

    const layoutConfig: LayoutConfig = {
      algorithm: layoutAlgorithm,
      direction: layoutDirection,
      nodeSpacing: 90,
      rankSpacing: 180,
      edgeLength: 150,
      groupPadding: 40,
      enableDomainClustering: showDomainBounds,
    }

    const result = calculateLayout(graphNodes, graphEdges, layoutConfig)

    // Clear loading indicator after a short delay
    setTimeout(() => setIsCalculatingLayout(false), 100)

    return result
  }, [graphNodes, graphEdges, layoutAlgorithm, layoutDirection, showDomainBounds, layoutKey])

  // Create initial nodes - computed once based on filtered classes
  const initialNodes = useMemo(() => {
    const classNodes = filteredClasses.map((cls) => ({
      id: cls.uri,
      type: 'classNode',
      position: positions[cls.uri] || { x: 0, y: 0 },
      data: {
        label: cls.localName,
        domain: cls.domain,
        description: cls.description,
        propertyCount: cls.properties.length,
        instanceCount: instanceCounts[cls.uri],
        isConnected,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    }))

    // Add domain boundary nodes if enabled
    const boundaryNodes = viewMode === 'schema'
      ? createDomainBoundaryNodes(domainBounds, showDomainBounds)
      : []

    return [...boundaryNodes, ...classNodes]
  }, [filteredClasses, positions, instanceCounts, isConnected, domainBounds, showDomainBounds, viewMode])

  // Create initial edges
  const initialEdges = useMemo(() => {
    const edges: Edge[] = []
    const classUris = new Set(filteredClasses.map((c) => c.uri))

    filteredClasses.forEach((cls) => {
      cls.properties
        .filter((p) => p.type === 'object' && classUris.has(p.range))
        .forEach((prop) => {
          edges.push({
            id: `${cls.uri}-${prop.uri}-${prop.range}`,
            source: cls.uri,
            target: prop.range,
            label: prop.localName,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#a19a90', strokeWidth: 1.5 },
            labelStyle: { fontSize: 9, fill: '#6b6560', fontWeight: 500 },
            labelBgStyle: { fill: '#fcfcf9', fillOpacity: 0.9 },
            labelBgPadding: [4, 2] as [number, number],
            labelBgBorderRadius: 4,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: '#a19a90',
            },
          })
        })
    })

    return edges
  }, [filteredClasses])

  // Knowledge graph nodes - transform graph data to ReactFlow nodes
  const knowledgeNodes = useMemo((): Node[] => {
    if (!graphData?.nodes) return []

    // Convert to GraphNode format
    const kgGraphNodes: GraphNode[] = graphData.nodes
      .filter(node => showFacets || node.typeLabel !== 'Facet')
      .map(node => ({
        uri: node.uri,
        label: node.label,
        domain: (node.domain as OntologyDomain) || 'solution',
        properties: 0,
        instanceCount: 0,
      }))

    const kgGraphEdges: GraphEdge[] = graphData.edges.map(edge => ({
      source: edge.source,
      target: edge.target,
      label: edge.predicateLabel,
    }))

    // Calculate positions using appropriate layout based on view mode
    const layoutConfig: LayoutConfig = {
      algorithm: viewMode === 'radial' ? 'radial' : 'force',
      nodeSpacing: 100,
      edgeLength: 150,
      enableDomainClustering: showDomainBounds,
    }
    const { positions } = calculateLayout(kgGraphNodes, kgGraphEdges, layoutConfig)

    return graphData.nodes
      .filter(node => showFacets || node.typeLabel !== 'Facet')
      .map((node): Node => {
        const isFacet = node.typeLabel === 'Facet'

        if (isFacet) {
          return {
            id: node.uri,
            type: 'facetNode',
            position: positions[node.uri] || { x: Math.random() * 800, y: Math.random() * 600 },
            data: {
              uri: node.uri,
              facetType: 'extracted',
              content: node.label,
              confidence: 0.85,
            } satisfies FacetNodeData,
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
          }
        }

        return {
          id: node.uri,
          type: 'instanceNode',
          position: positions[node.uri] || { x: Math.random() * 800, y: Math.random() * 600 },
          data: {
            uri: node.uri,
            label: node.label,
            typeLabel: node.typeLabel,
            domain: (node.domain as OntologyDomain) || 'solution',
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
        }
      })
  }, [graphData, showFacets, viewMode, showDomainBounds, layoutKey])

  // Knowledge graph edges - transform graph data to ReactFlow edges
  const knowledgeEdges = useMemo(() => {
    if (!graphData?.edges) return []

    const nodeUris = new Set(knowledgeNodes.map(n => n.id))

    // Debug: check edge filtering
    const filteredEdges = graphData.edges.filter(edge => {
      const sourceExists = nodeUris.has(edge.source)
      const targetExists = nodeUris.has(edge.target)
      if (!sourceExists || !targetExists) {
        console.log('[KG Edges] Filtered out edge:', {
          source: edge.source.slice(-40),
          target: edge.target.slice(-40),
          sourceExists,
          targetExists,
        })
      }
      return sourceExists && targetExists
    })

    console.log('[KG] graphData.edges:', graphData.edges.length, 'nodeUris:', nodeUris.size, 'filteredEdges:', filteredEdges.length)

    return filteredEdges.map((edge, index) => ({
        id: `kg-edge-${index}`,
        source: edge.source,
        target: edge.target,
        label: edge.predicateLabel,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#7a8c99', strokeWidth: 1.5 },
        labelStyle: { fontSize: 9, fill: '#4a5568', fontWeight: 500 },
        labelBgStyle: { fill: '#fcfcf9', fillOpacity: 0.9 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 10,
          height: 10,
          color: '#7a8c99',
        },
      }))
  }, [graphData, knowledgeNodes])

  // Use ReactFlow state hooks with initial values
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  // Track if we've initialized to prevent update loops
  const prevNodesKey = useRef<string>('')
  const prevViewMode = useRef<GraphViewMode>('schema')

  // Callback to capture ReactFlow instance on initialization
  const onInit = useCallback((instance: any) => {
    reactFlowInstanceRef.current = instance
  }, [])

  // Update nodes when view mode changes or data changes
  useEffect(() => {
    if (viewMode === 'schema') {
      const nodesKey = 'schema-' + JSON.stringify(
        filteredClasses.map((c) => c.uri).sort()
      ) + JSON.stringify(instanceCounts)

      if (nodesKey !== prevNodesKey.current || prevViewMode.current !== viewMode) {
        prevNodesKey.current = nodesKey
        prevViewMode.current = viewMode
        setNodes(initialNodes)
        setEdges(initialEdges)

        // Fit view after a short delay to ensure nodes are rendered
        setTimeout(() => {
          reactFlowInstanceRef.current?.fitView({ padding: 0.15, duration: 200 })
        }, 50)
      }
    } else {
      // Knowledge graph or radial mode
      const nodesKey = viewMode + '-' + JSON.stringify(knowledgeNodes.map(n => n.id).sort())

      if (nodesKey !== prevNodesKey.current || prevViewMode.current !== viewMode) {
        prevNodesKey.current = nodesKey
        prevViewMode.current = viewMode
        setNodes(knowledgeNodes as unknown as typeof nodes)
        setEdges(knowledgeEdges as Edge[])

        // Fit view after a short delay to ensure nodes are rendered
        setTimeout(() => {
          reactFlowInstanceRef.current?.fitView({ padding: 0.15, duration: 200 })
        }, 50)
      }
    }
  }, [viewMode, initialNodes, initialEdges, knowledgeNodes, knowledgeEdges, filteredClasses, instanceCounts, setNodes, setEdges])

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (node.type === 'classNode') {
      setSelectedNodeUri(node.id)
      setSelectedNodeType('class')
    } else if (node.type === 'instanceNode') {
      setSelectedNodeUri(node.id)
      setSelectedNodeType('instance')
    } else if (node.type === 'facetNode') {
      setSelectedNodeUri(node.id)
      setSelectedNodeType('facet')
    }
  }, [])

  const handleToggleExpand = useCallback((classUri: string) => {
    setExpandedClasses((prev) => {
      const next = new Set(prev)
      if (next.has(classUri)) {
        next.delete(classUri)
      } else {
        next.add(classUri)
      }
      return next
    })
  }, [])

  const handleNavigate = useCallback((uri: string, type: 'class' | 'instance' | 'facet') => {
    setSelectedNodeUri(uri)
    setSelectedNodeType(type)
  }, [])

  // Get available categories for current domain
  const availableCategories = useMemo(() => {
    if (!filterDomain) {
      // If no domain filter, show categories from all classes
      const allCategories = new Map<string, OntologyCategory>()
      ALL_CLASSES.forEach((cls) => {
        const cat = getCategoryForClass(cls.localName)
        if (cat) allCategories.set(cat.id, cat)
      })
      return Array.from(allCategories.values())
    }
    return getCategoriesForDomain(filterDomain)
  }, [filterDomain])

  const handleToggleCategory = useCallback((categoryId: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }, [])

  const handleClearCategories = useCallback(() => {
    setSelectedCategories(new Set())
  }, [])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: 'var(--color-surface)',
        borderRadius: '8px',
        border: '1px solid var(--color-border)',
      }}>
        {/* View Mode Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontWeight: 500 }}>View:</span>
          <div style={{
            display: 'flex',
            backgroundColor: 'var(--color-warm-100)',
            borderRadius: '6px',
            padding: '2px',
          }}>
            <button
              onClick={() => setViewMode('schema')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: viewMode === 'schema' ? 'var(--color-surface)' : 'transparent',
                color: viewMode === 'schema' ? 'var(--color-text)' : 'var(--color-text-muted)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: viewMode === 'schema' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <GitBranch style={{ width: '14px', height: '14px' }} />
              Schema
            </button>
            <button
              onClick={() => setViewMode('knowledge')}
              disabled={!isConnected}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: viewMode === 'knowledge' ? 'var(--color-surface)' : 'transparent',
                color: viewMode === 'knowledge' ? 'var(--color-business)' : 'var(--color-text-muted)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: isConnected ? 'pointer' : 'not-allowed',
                boxShadow: viewMode === 'knowledge' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                opacity: isConnected ? 1 : 0.5,
                transition: 'all 0.15s',
              }}
            >
              <Network style={{ width: '14px', height: '14px' }} />
              Knowledge Graph
            </button>
            <button
              onClick={() => setViewMode('radial')}
              disabled={!isConnected}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: viewMode === 'radial' ? 'var(--color-surface)' : 'transparent',
                color: viewMode === 'radial' ? 'var(--color-solution)' : 'var(--color-text-muted)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: isConnected ? 'pointer' : 'not-allowed',
                boxShadow: viewMode === 'radial' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                opacity: isConnected ? 1 : 0.5,
                transition: 'all 0.15s',
              }}
            >
              <CircleDot style={{ width: '14px', height: '14px' }} />
              Radial
            </button>
          </div>
        </div>

        {/* Layout Controls - Schema View Only */}
        {viewMode === 'schema' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '12px', borderLeft: '1px solid var(--color-border)' }}>
            {/* Layout Algorithm Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Layout style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)' }} />
              <select
                value={layoutAlgorithm}
                onChange={(e) => setLayoutAlgorithm(e.target.value as LayoutAlgorithm)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  fontSize: '11px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <option value="optimized">Optimized</option>
                <option value="dagre">Hierarchical</option>
                <option value="force">Force-Directed</option>
                <option value="radial">Radial</option>
              </select>
            </div>

            {/* Direction Selector - only for dagre */}
            {layoutAlgorithm === 'dagre' && (
              <select
                value={layoutDirection}
                onChange={(e) => setLayoutDirection(e.target.value as LayoutDirection)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  fontSize: '11px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <option value="LR">Left → Right</option>
                <option value="TB">Top → Bottom</option>
                <option value="RL">Right → Left</option>
                <option value="BT">Bottom → Top</option>
              </select>
            )}

            {/* Domain Bounds Toggle */}
            <button
              onClick={() => setShowDomainBounds(!showDomainBounds)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: showDomainBounds ? 'rgba(92, 138, 107, 0.1)' : 'transparent',
                color: showDomainBounds ? 'var(--color-market)' : 'var(--color-text-muted)',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <Layers style={{ width: '14px', height: '14px' }} />
              Domains
            </button>

            {/* Re-layout Button */}
            <button
              onClick={() => setLayoutKey(k => k + 1)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-muted)',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              title="Re-calculate layout"
            >
              <RotateCw style={{ width: '14px', height: '14px' }} />
            </button>
          </div>
        )}

        {/* Knowledge Graph / Radial Controls */}
        {(viewMode === 'knowledge' || viewMode === 'radial') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setShowFacets(!showFacets)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: showFacets ? 'rgba(255, 192, 95, 0.1)' : 'transparent',
                color: showFacets ? '#d4a020' : 'var(--color-text-muted)',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {showFacets ? <Eye style={{ width: '14px', height: '14px' }} /> : <EyeOff style={{ width: '14px', height: '14px' }} />}
              Facets
            </button>
            {graphLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-text-muted)', fontSize: '11px' }}>
                <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                Loading...
              </div>
            )}
            {!graphLoading && graphData && (
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                {graphData.nodes.length} nodes · {graphData.edges.length} edges
              </span>
            )}
          </div>
        )}

        {/* Schema View Info */}
        {viewMode === 'schema' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
              {filteredClasses.length} classes · {initialEdges.length} relationships
            </span>
          </div>
        )}
      </div>

      {/* Category Filter Bar - Schema View Only */}
      {viewMode === 'schema' && availableCategories.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: 'var(--color-warm-50)',
          borderRadius: '8px',
          border: '1px solid var(--color-border-subtle)',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
            Filter by Category:
          </span>
          {availableCategories.map((category) => {
            const isSelected = selectedCategories.has(category.id)
            const domainColor = DOMAIN_COLORS[category.domain]

            return (
              <button
                key={category.id}
                onClick={() => handleToggleCategory(category.id)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${isSelected ? domainColor : 'var(--color-border)'}`,
                  backgroundColor: isSelected ? `${domainColor}1A` : 'var(--color-surface)',
                  color: isSelected ? domainColor : 'var(--color-text-muted)',
                  fontSize: '11px',
                  fontWeight: isSelected ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
                title={category.description}
              >
                {category.label}
              </button>
            )
          })}
          {selectedCategories.size > 0 && (
            <button
              onClick={handleClearCategories}
              style={{
                padding: '4px 10px',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-muted)',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                marginLeft: '4px',
              }}
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {/* Graph Container */}
      <div style={{ flex: 1, display: 'flex', gap: '16px', minHeight: 0 }}>
        {/* Graph Panel */}
        <div style={{ flex: 1, minWidth: 0, borderRadius: '12px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)', overflow: 'hidden', position: 'relative' }}>
          {/* Loading Overlay */}
          {isCalculatingLayout && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(253, 252, 250, 0.8)',
              backdropFilter: 'blur(2px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              zIndex: 1000,
            }}>
              <div style={{ animation: 'spin 1s linear infinite' }}>
                <Loader2 style={{ width: '32px', height: '32px', color: 'var(--color-primary)' }} />
              </div>
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                Calculating {layoutAlgorithm === 'dagre' ? 'hierarchical' : layoutAlgorithm === 'force' ? 'force-directed' : layoutAlgorithm} layout...
              </span>
              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onInit={onInit}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.3}
            maxZoom={1.5}
            nodesDraggable={viewMode === 'knowledge' || viewMode === 'radial'}
            nodesConnectable={false}
            elementsSelectable={true}
            defaultEdgeOptions={{
              type: 'smoothstep',
            }}
          >
            <Background color="#e5e4e0" gap={20} />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'facetNode') return '#d4a020'
                const domain = node.data?.domain as OntologyDomain
                return DOMAIN_COLORS[domain] || '#a19a90'
              }}
              maskColor="rgba(253, 252, 250, 0.8)"
              style={{ borderRadius: '8px' }}
            />
          </ReactFlow>
        </div>

        {/* Detail Panel */}
        {selectedNodeUri && selectedNodeType && (
          <DetailPanel
            nodeUri={selectedNodeUri}
            nodeType={selectedNodeType}
            isConnected={isConnected}
            expandedClasses={expandedClasses}
            onClose={() => {
              setSelectedNodeUri(null)
              setSelectedNodeType(null)
            }}
            onNavigate={handleNavigate}
            onToggleExpand={handleToggleExpand}
          />
        )}
      </div>
    </div>
  )
}

interface DetailPanelProps {
  nodeUri: string
  nodeType: 'class' | 'instance' | 'facet'
  isConnected: boolean
  expandedClasses: Set<string>
  onClose: () => void
  onNavigate: (uri: string, type: 'class' | 'instance' | 'facet') => void
  onToggleExpand: (classUri: string) => void
}

function DetailPanel({
  nodeUri,
  nodeType,
  isConnected,
  expandedClasses,
  onClose,
  onNavigate,
  onToggleExpand,
}: DetailPanelProps) {
  if (nodeType === 'class') {
    return (
      <ClassDetailPanel
        classUri={nodeUri}
        isConnected={isConnected}
        isExpanded={expandedClasses.has(nodeUri)}
        onClose={onClose}
        onNavigate={onNavigate}
        onToggleExpand={onToggleExpand}
      />
    )
  }

  if (nodeType === 'facet') {
    return (
      <FacetDetailPanel
        facetUri={nodeUri}
        onClose={onClose}
        onNavigate={onNavigate}
      />
    )
  }

  return (
    <InstanceDetailPanel
      instanceUri={nodeUri}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  )
}

interface ClassDetailPanelProps {
  classUri: string
  isConnected: boolean
  isExpanded: boolean
  onClose: () => void
  onNavigate: (uri: string, type: 'class' | 'instance' | 'facet') => void
  onToggleExpand: (classUri: string) => void
}

function ClassDetailPanel({
  classUri,
  isConnected,
  isExpanded,
  onClose,
  onNavigate,
  onToggleExpand,
}: ClassDetailPanelProps) {
  const ontologyClass = ALL_CLASSES.find((c) => c.uri === classUri)
  const { data: instances = [], isLoading } = useInstancesOfClass(isConnected ? classUri : null)

  if (!ontologyClass) return null

  return (
    <Card style={{ width: '350px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CardTitle style={{ fontSize: '16px', fontFamily: 'monospace' }}>
              {ontologyClass.localName}
            </CardTitle>
            <Badge
              style={{
                marginTop: '8px',
                backgroundColor: DOMAIN_COLORS[ontologyClass.domain],
                color: 'white',
              }}
            >
              {ontologyClass.domain}
            </Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} style={{ flexShrink: 0 }}>
            <X style={{ width: '16px', height: '16px' }} />
          </Button>
        </div>
        {ontologyClass.description && (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '12px', lineHeight: 1.5 }}>
            {ontologyClass.description}
          </p>
        )}
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Properties */}
          {ontologyClass.properties.length > 0 && (
            <div>
              <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Properties ({ontologyClass.properties.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {ontologyClass.properties.map((prop) => (
                  <div
                    key={prop.uri}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: 'var(--color-warm-50)',
                      borderRadius: '6px',
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{prop.localName}</div>
                    <div style={{ color: 'var(--color-text-muted)', marginTop: '2px' }}>
                      {prop.type === 'object' ? (
                        <button
                          onClick={() => onNavigate(prop.range, 'class')}
                          style={{
                            color: 'var(--color-primary)',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          → {getLocalName(prop.range)}
                        </button>
                      ) : (
                        <span>{getLocalName(prop.range)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instances */}
          {isConnected && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  cursor: 'pointer',
                }}
                onClick={() => onToggleExpand(classUri)}
              >
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-business)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isExpanded ? <ChevronDown style={{ width: '14px', height: '14px' }} /> : <ChevronRight style={{ width: '14px', height: '14px' }} />}
                  Instances ({instances.length})
                </h4>
              </div>

              {isExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Loader2 style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : instances.length === 0 ? (
                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '12px' }}>
                      No instances found
                    </p>
                  ) : (
                    instances.map((instance) => (
                      <div
                        key={instance.uri}
                        onClick={() => onNavigate(instance.uri, 'instance')}
                        style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(49, 150, 226, 0.1)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '1px solid transparent',
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-business)')}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'transparent')}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-business)' }}>
                          {instance.label}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                          {getLocalName(instance.uri)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {!isConnected && (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '16px', backgroundColor: 'var(--color-warm-50)', borderRadius: '8px' }}>
              Connect to Fuseki in Settings to view instances
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface InstanceDetailPanelProps {
  instanceUri: string
  onClose: () => void
  onNavigate: (uri: string, type: 'class' | 'instance' | 'facet') => void
}

function InstanceDetailPanel({ instanceUri, onClose, onNavigate }: InstanceDetailPanelProps) {
  const { data, isLoading } = useInstanceDetails(instanceUri)
  const { facets, count: facetCount, isLoading: facetsLoading } = useEntityFacets(instanceUri)
  const [facetsExpanded, setFacetsExpanded] = useState(true)

  return (
    <Card style={{ width: '350px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CardTitle style={{ fontSize: '14px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {getLocalName(instanceUri)}
            </CardTitle>
            {data?.types && data.types.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                {data.types.map((type) => (
                  <Badge
                    key={type.uri}
                    variant="outline"
                    style={{ fontSize: '10px', cursor: 'pointer' }}
                    onClick={() => onNavigate(type.uri, 'class')}
                  >
                    {type.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} style={{ flexShrink: 0 }}>
            <X style={{ width: '16px', height: '16px' }} />
          </Button>
        </div>
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Properties */}
            {Object.keys(data.properties).length > 0 && (
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Properties
                </h4>
                <div style={{ backgroundColor: 'var(--color-warm-50)', borderRadius: '8px', padding: '12px' }}>
                  {Object.entries(data.properties).map(([propUri, values]) => (
                    <div key={propUri} style={{ paddingBottom: '8px', marginBottom: '8px', borderBottom: '1px solid var(--color-border-subtle)' }}>
                      <div style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                        {getLocalName(propUri)}
                      </div>
                      {values.map((v, i) => (
                        <div key={i} style={{ fontSize: '13px' }}>
                          {v.value.startsWith('http') ? (
                            <button
                              onClick={() => onNavigate(v.value, 'instance')}
                              style={{
                                color: 'var(--color-primary)',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textAlign: 'left',
                              }}
                            >
                              {v.label || getLocalName(v.value)}
                            </button>
                          ) : (
                            <span>{v.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outgoing Relationships */}
            {data.outgoing.length > 0 && (
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-business)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ArrowRight style={{ width: '14px', height: '14px' }} />
                  OUTGOING
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.outgoing.map((rel, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: 'rgba(49, 150, 226, 0.1)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                      onClick={() => onNavigate(rel.target, 'instance')}
                    >
                      <Badge style={{ fontSize: '10px', backgroundColor: 'var(--color-business)', color: 'white' }}>
                        {getLocalName(rel.property)}
                      </Badge>
                      <ArrowRight style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
                      <span style={{ fontSize: '12px', color: 'var(--color-business)' }}>
                        {rel.targetLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Incoming Relationships */}
            {data.incoming.length > 0 && (
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-market)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ArrowLeft style={{ width: '14px', height: '14px' }} />
                  INCOMING
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.incoming.map((rel, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: 'rgba(92, 138, 107, 0.1)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                      onClick={() => onNavigate(rel.source, 'instance')}
                    >
                      <span style={{ fontSize: '12px', color: 'var(--color-market)' }}>
                        {rel.sourceLabel}
                      </span>
                      <ArrowRight style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
                      <Badge style={{ fontSize: '10px', backgroundColor: 'var(--color-market)', color: 'white' }}>
                        {getLocalName(rel.property)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Linked Facets */}
            <div>
              <button
                onClick={() => setFacetsExpanded(!facetsExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--color-solution)',
                  marginBottom: '8px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FileText style={{ width: '14px', height: '14px' }} />
                  Linked Facets ({facetCount})
                </span>
                {facetsExpanded ? (
                  <ChevronDown style={{ width: '14px', height: '14px' }} />
                ) : (
                  <ChevronRight style={{ width: '14px', height: '14px' }} />
                )}
              </button>

              {facetsExpanded && (
                <div style={{ backgroundColor: 'var(--color-warm-50)', borderRadius: '8px', padding: '12px' }}>
                  {facetsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : facets.length === 0 ? (
                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '8px' }}>
                      No linked facets
                    </p>
                  ) : (
                    <LinkedFacetsList
                      facets={facets}
                      compact
                      maxVisible={3}
                      emptyMessage="No linked facets"
                    />
                  )}
                </div>
              )}
            </div>

            {/* No data message */}
            {Object.keys(data.properties).length === 0 &&
              data.outgoing.length === 0 &&
              data.incoming.length === 0 &&
              facetCount === 0 && (
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '16px' }}>
                  No properties, relationships, or linked facets found.
                </p>
              )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// FACET DETAIL PANEL
// Shows facet details when a facet node is selected in the graph
// ============================================================================

interface FacetDetailPanelProps {
  facetUri: string
  onClose: () => void
  onNavigate: (uri: string, type: 'class' | 'instance' | 'facet') => void
}

function FacetDetailPanel({ facetUri, onClose, onNavigate }: FacetDetailPanelProps) {
  // For now, show basic facet info from the URI
  // In full implementation, this would fetch facet details from the graph
  const facetLocalName = getLocalName(facetUri)

  // Extract facet type from URI if possible (format: Facet_TYPE_timestamp_id)
  const uriParts = facetLocalName.split('_')
  const facetType = uriParts.length > 1 ? uriParts[1] : 'unknown'
  const facetConfig = FACET_TYPE_CONFIG[facetType as FacetType]

  return (
    <Card style={{ width: '350px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileText style={{ width: '18px', height: '18px', color: facetConfig?.color || '#d4a020' }} />
              <CardTitle style={{ fontSize: '14px' }}>
                {facetConfig?.label || 'Facet'}
              </CardTitle>
            </div>
            <Badge
              style={{
                backgroundColor: facetConfig?.color || '#d4a020',
                color: 'white',
                fontSize: '10px',
              }}
            >
              {facetConfig?.category || 'insights'}
            </Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} style={{ flexShrink: 0 }}>
            <X style={{ width: '16px', height: '16px' }} />
          </Button>
        </div>
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Facet URI */}
          <div>
            <h4 style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>
              URI
            </h4>
            <p style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>
              {facetUri}
            </p>
          </div>

          {/* Description */}
          {facetConfig?.description && (
            <div>
              <h4 style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>
                Type Description
              </h4>
              <p style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.5 }}>
                {facetConfig.description}
              </p>
            </div>
          )}

          {/* Suggested Entity Types */}
          {facetConfig?.ontologyTypes && facetConfig.ontologyTypes.length > 0 && (
            <div>
              <h4 style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>
                Can Create Entities
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {facetConfig.ontologyTypes.map((type) => (
                  <Badge key={type} variant="outline" style={{ fontSize: '10px' }}>
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '16px', backgroundColor: 'var(--color-warm-50)', borderRadius: '8px' }}>
            Facet details are loaded from the source document. View the source to see full content and evidence.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
